/**
 * ChoroplethMap — a US-states choropleth (the right chart for geospatial data,
 * per the 2026-06-13 chart redesign). Shades each state by a metric value, so a
 * geographic pattern reads instantly where a table or ranked bars can't.
 *
 * Theme-safe by construction: states fill with `var(--good)` at a scaled
 * fill-opacity (light → dark with the value), so it adapts to light/dark mode
 * without any JS color logic. d3-geo + topojson + the us-atlas topology are
 * dynamic-imported, so the ~hefty topology only loads on the cards that show a
 * map (code-split out of the main bundle).
 *
 * `values` is keyed by USPS code ("CA", "TX", …) to match the backend geography
 * payload; we map those to the full state names the topology uses.
 */

import { useEffect, useMemo, useState } from "react";
import { moneyFmt } from "./cards.js";

const USPS_TO_NAME: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado",
  CT: "Connecticut", DE: "Delaware", DC: "District of Columbia", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky",
  LA: "Louisiana", ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota",
  MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire",
  NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming", PR: "Puerto Rico",
};

interface Shape { name: string; d: string }

const NAME_TO_USPS: Record<string, string> = Object.fromEntries(
  Object.entries(USPS_TO_NAME).map(([usps, name]) => [name, usps]),
);

export function ChoroplethMap({ values, money = true, valueLabel = "value", onPick }: {
  /** Keyed by USPS code (CA, TX, …). */
  values: Record<string, number>;
  money?: boolean;
  valueLabel?: string;
  /** Click a state → its USPS code (for drill-down). */
  onPick?: (usps: string) => void;
}): React.ReactElement {
  const [shapes, setShapes] = useState<Shape[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [hover, setHover] = useState<{ name: string; x: number; y: number } | null>(null);

  const byName = useMemo(() => {
    const m: Record<string, number> = {};
    for (const [usps, v] of Object.entries(values)) {
      const name = USPS_TO_NAME[usps.toUpperCase()];
      if (name) m[name] = v;
    }
    return m;
  }, [values]);

  const max = useMemo(() => Math.max(1, ...Object.values(byName)), [byName]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      import("d3-geo"),
      import("topojson-client"),
      import("us-atlas/states-10m.json"),
    ])
      .then(([d3geo, topo, atlas]) => {
        if (!alive) return;
        const us = (atlas as { default?: unknown }).default ?? atlas;
        const states = (us as { objects: { states: unknown } }).objects.states;
        const featFn = topo.feature as unknown as (t: unknown, o: unknown) => { features: Array<{ properties: { name: string } }> };
        const fc = featFn(us, states);
        // fitSize auto-fits ALL features (incl. the Alaska/Hawaii insets that
        // geoAlbersUsa positions) into the viewBox — no hand-tuned scale (Codex).
        const proj = (d3geo.geoAlbersUsa() as unknown as { fitSize: (s: [number, number], o: unknown) => unknown }).fitSize([960, 570], fc);
        const pathFn = d3geo.geoPath(proj as Parameters<typeof d3geo.geoPath>[0]) as unknown as (g: unknown) => string | null;
        const out: Shape[] = [];
        for (const f of fc.features) {
          const d = pathFn(f);
          if (d) out.push({ name: f.properties.name, d });
        }
        setShapes(out);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  const fmt = (v: number): string => (money ? moneyFmt(v) : Math.round(v).toLocaleString());

  if (failed) return <div style={{ color: "var(--faint)", padding: "18px 0", fontSize: 12.5 }}>Map unavailable.</div>;
  if (!shapes) return <div style={{ color: "var(--faint)", padding: "18px 0", fontSize: 12.5 }}>Loading map…</div>;

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox="0 0 960 570" width="100%" style={{ display: "block" }} role="img" aria-label={`US map shaded by ${valueLabel} per state`}>
        {shapes.map((s) => {
          const v = byName[s.name] ?? 0;
          // Sequential ramp by MIXING the metric color into the surface (not
          // alpha) — reads correctly + linearly in both light and dark (Codex).
          const pct = v > 0 ? Math.round(16 + 80 * (v / max)) : 0;
          const usps = NAME_TO_USPS[s.name];
          const clickable = !!onPick && usps !== undefined && v > 0;
          return (
            <path
              key={s.name}
              d={s.d}
              fill={v > 0 ? `color-mix(in srgb, var(--good) ${pct}%, var(--surface-2))` : "var(--surface-2)"}
              stroke="var(--surface)"
              strokeWidth={0.6}
              style={clickable ? { cursor: "pointer" } : undefined}
              onClick={clickable ? () => onPick?.(usps) : undefined}
              onMouseEnter={(e) => setHover({ name: s.name, x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY })}
              onMouseMove={(e) => setHover({ name: s.name, x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY })}
              onMouseLeave={() => setHover(null)}
            />
          );
        })}
      </svg>
      {/* legend */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 11, color: "var(--body-faint)" }}>
        <span>{valueLabel} · low</span>
        <span style={{ flex: "0 0 90px", height: 8, borderRadius: 4, background: "linear-gradient(90deg, color-mix(in srgb, var(--good) 16%, transparent), var(--good))" }} />
        <span>high</span>
      </div>
      {hover && (
        <div style={{ position: "absolute", left: `${(hover.x / 960) * 100}%`, top: hover.y, transform: "translate(8px, -110%)", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "var(--shadow-pop)", padding: "6px 9px", fontSize: 12, pointerEvents: "none", whiteSpace: "nowrap", zIndex: 5 }}>
          <span style={{ color: "var(--text)", fontWeight: 600 }}>{hover.name}</span>
          <span className="dd-num" style={{ color: "var(--num)", marginLeft: 8 }}>{fmt(byName[hover.name] ?? 0)}</span>
        </div>
      )}
    </div>
  );
}
